import { CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { FaPodcast, FaSpotify, FaYoutube, FaInstagram, FaLinkedinIn } from "react-icons/fa6";
import { NAV_LINKS, BOOK_URL, APPLE_PODCAST_URL, SPOTIFY_URL, BRAND_NAME, YOUTUBE_URL, INSTAGRAM_URL, LINKEDIN_URL } from "@/lib/site";
import { LEGAL_FOOTER_LINKS } from "@/lib/legal";
import logoFull from "@/assets/logo-light-horizontal.png";

const resourceLinks = [
  { label: "From Regular to Rich (Book)", href: BOOK_URL, external: true },
  { label: "Free Resources", href: "/resources", external: false },
  { label: "Mastering Money Mondays", href: "#newsletter", external: false },
];

const connectLinks = [
  { label: "YouTube", href: YOUTUBE_URL, icon: FaYoutube },
  { label: "Instagram", href: INSTAGRAM_URL, icon: FaInstagram },
  { label: "LinkedIn", href: LINKEDIN_URL, icon: FaLinkedinIn },
  { label: "Apple Podcasts", href: APPLE_PODCAST_URL, icon: FaPodcast },
  { label: "Spotify", href: SPOTIFY_URL, icon: FaSpotify },
];

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-legacy text-ivory">
      <div className="container-lr py-16 md:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.45fr_0.8fr_0.9fr_0.9fr_1fr] lg:gap-10">
          <div>
            <img src={logoFull} alt={BRAND_NAME} data-testid="footer-logo" className="h-16 w-auto object-contain md:h-20" />
            <p className="mt-6 max-w-sm text-taupe text-sm leading-relaxed">Helping hardworking families build financial systems that create more control today and more opportunities tomorrow.</p>
            <p className="mt-4 max-w-sm font-serif text-xl italic text-gold">The information is free, but the knowledge is priceless.</p>
          </div>
          <div>
            <p className="eyebrow text-gold">Explore</p>
            <ul className="mt-5 space-y-3">{NAV_LINKS.filter((l) => l.label !== "Home").map((link) => <li key={link.label}><Link to={link.href} data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-taupe text-sm hover:text-gold transition-colors focus-ring rounded-sm">{link.label}</Link></li>)}</ul>
          </div>
          <div>
            <p className="eyebrow text-gold">Resources</p>
            <ul className="mt-5 space-y-3">{resourceLinks.map((link) => <li key={link.label}><a href={link.href} target={link.external ? "_blank" : undefined} rel={link.external ? "noopener noreferrer" : undefined} className="text-taupe text-sm hover:text-gold transition-colors focus-ring rounded-sm">{link.label}</a></li>)}</ul>
          </div>
          <div>
            <p className="eyebrow text-gold">Get Started</p>
            <p className="mt-5 text-taupe text-sm leading-relaxed">Begin with a free, education-first strategy call.</p>
            <Link to="/strategy-call" data-testid="footer-cta-button" className="btn-base btn-gold mt-6 w-full"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
          </div>
          <div>
            <p className="eyebrow text-gold">Connect</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {connectLinks.map(({ label, href, icon: Icon }) => <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ivory/15 text-taupe transition-colors hover:border-gold/50 hover:text-gold focus-ring"><Icon size={18} /></a>)}
            </div>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-ivory/12 pt-7 md:flex-row md:items-center">
          <p className="text-taupe/70 text-xs tracking-wide">© {year} {BRAND_NAME}. All rights reserved.</p>
          <p className="text-taupe/70 text-xs tracking-wide">Authorized Infinite Banking Concepts Practitioner.</p>
        </div>
        <nav aria-label="Legal" className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
          {LEGAL_FOOTER_LINKS.map((l) => <Link key={l.to} to={l.to} data-testid={`footer-legal-${l.to.replace("/", "")}`} className="text-taupe/60 text-xs tracking-wide hover:text-gold transition-colors focus-ring rounded-sm">{l.label}</Link>)}
        </nav>
      </div>
    </footer>
  );
};
